import { useState, useMemo } from 'react';
import * as AppPics from 'assets/images';
import TablePaginate from 'components/Paginate';
import StatusSelect, { STAR_OPTIONS } from './StatusSelect';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import useGetAdminRatings from 'hooks/API/GET/useGetAdminRatings';
import useGetRatingStats from 'hooks/API/GET/useGetRatingStats';

const STAR_COLOR = '#F59E0B';

function StarDisplay({ rating }) {
  return (
    <span aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} style={{ color: n <= rating ? STAR_COLOR : '#ddd', fontSize: 17 }}>
          ★
        </span>
      ))}
    </span>
  );
}

function RatingBar({ star, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
      <span style={{ width: 22, textAlign: 'right', fontSize: 13, color: '#555' }}>{star}★</span>
      <div
        style={{ flex: 1, height: 10, background: '#e8e8e8', borderRadius: 5, overflow: 'hidden' }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: STAR_COLOR,
            borderRadius: 5,
            transition: 'width 0.3s',
          }}
        />
      </div>
      <span style={{ width: 36, fontSize: 13, color: '#555' }}>{count}</span>
    </div>
  );
}

function RatingsTab() {
  const [params, setParams] = usePaginateDefaults();
  const [enableFilter, setEnableFilter] = useState(false);
  const [filterMinRating, setFilterMinRating] = useState('');
  const [filterMaxRating, setFilterMaxRating] = useState('');

  const queryParams = useMemo(
    () => ({
      ...params,
      minRating: filterMinRating || undefined,
      maxRating: filterMaxRating || undefined,
    }),
    [params, filterMinRating, filterMaxRating]
  );

  const { data: ratingsData, isLoading } = useGetAdminRatings({
    enabled: true,
    config: { params: queryParams },
    queryKey: ['admin-user-ratings', queryParams],
  });

  const { data: statsData } = useGetRatingStats({ enabled: true });

  const ratings = ratingsData?.data?.result ?? [];
  const { page = 1, total = 0 } = ratingsData?.data?.meta ?? {};
  const stats = statsData?.data ?? { average: 0, total: 0, distribution: {} };

  const clearFilters = () => {
    setFilterMinRating('');
    setFilterMaxRating('');
    setParams((prev) => ({ ...prev, page: 0 }));
    setEnableFilter(false);
  };

  const hasActiveFilters = filterMinRating || filterMaxRating;

  return (
    <>
      {/* ── Stats summary card ── */}
      <div
        className="mb-4 p-4"
        style={{
          background: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: 10,
          maxWidth: 500,
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: 24 }}>
          <div style={{ textAlign: 'center', minWidth: 80 }}>
            <div style={{ fontSize: 44, fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
              {stats.average?.toFixed(1) ?? '—'}
            </div>
            <StarDisplay rating={Math.round(stats.average ?? 0)} />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              {stats.total} rating{stats.total !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {[5, 4, 3, 2, 1].map((star) => (
              <RatingBar
                key={star}
                star={star}
                count={stats.distribution?.[star] ?? 0}
                total={stats.total ?? 0}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Search + Filter header ── */}
      <div className="tab__contentMenu mb-3">
        <ul className="tab__contentMenu-list">
          <li>
            <h2 className="prime-head p-0 boldtitle text-nowrap" style={{ fontSize: 16 }}>
              All Ratings
            </h2>
          </li>
        </ul>

        <div className="mainHeader__search">
          <ul className="tab__contentMenu-list">
            <li className="px-4 flex items-center">
              <button
                type="button"
                onClick={() => setEnableFilter(!enableFilter)}
                className="flex items-center gap-1"
              >
                <img src={AppPics.TabFilterSvg} alt="Filter" />
                <span className="viewsettingtitle">Filter</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Filter panel ── */}
      {enableFilter && (
        <div className="row mt-2 mb-3">
          <div className="col-lg-3">
            <StatusSelect
              options={STAR_OPTIONS}
              value={filterMinRating}
              onChange={(v) => {
                setFilterMinRating(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              label="Minimum Stars"
              isClearable
              placeholder="Any"
            />
          </div>

          <div className="col-lg-3">
            <StatusSelect
              options={STAR_OPTIONS}
              value={filterMaxRating}
              onChange={(v) => {
                setFilterMaxRating(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              label="Maximum Stars"
              isClearable
              placeholder="Any"
            />
          </div>

          <div className="col-lg-3 d-flex" style={{ alignItems: 'flex-end' }}>
            <button type="button" className="btn btn-plane p--tint" onClick={clearFilters}>
              Clear filter
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="tab__contentBody">
        <div className="tableWrap">
          <div className="table-responsive">
            <table className="table tableWrap__table">
              <thead className="thead-light __appTheadColor">
                <tr>
                  <th className="text-center">#</th>
                  <th>User</th>
                  <th className="text-center">Rating</th>
                  <th>Review</th>
                  <th className="text-center">Platform</th>
                  <th className="text-center">Date</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      Loading…
                    </td>
                  </tr>
                ) : ratings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      No ratings yet.
                    </td>
                  </tr>
                ) : (
                  ratings.map((r, idx) => (
                    <tr key={r._id}>
                      <td className="text-center">{(page - 1) * params.limit + idx + 1}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.userId?.name ?? '—'}</div>
                        <small className="text-muted">
                          {r.userId?.email ?? r.userId?.mobilFull ?? ''}
                        </small>
                      </td>
                      <td className="text-center">
                        <StarDisplay rating={r.rating} />
                      </td>
                      <td style={{ maxWidth: 280 }}>
                        {r.review ? (
                          <span style={{ color: '#333', fontSize: 13 }}>{r.review}</span>
                        ) : (
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        {r.platform ? (
                          <span
                            className={`badge ${
                              r.platform === 'ios' ? 'badge-dark' : 'badge-success'
                            }`}
                          >
                            {r.platform}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {new Date(r.updatedAt ?? r.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TablePaginate
          currentPage={page}
          maxCount={total}
          defaultLimit={params.limit}
          setPage={(p) => setParams((prev) => ({ ...prev, page: p }))}
          handleChange={(limit) => setParams((prev) => ({ ...prev, limit, page: 0 }))}
        />
      </div>
    </>
  );
}

export default RatingsTab;
